import { styled } from '@linaria/react';
import { forwardRef } from 'react';
import { ResponsiveContainer, Tooltip, Treemap } from 'recharts';
import i18n from '../../../../../../base-dictionary';
import { ContentPlaceholder } from '../../../../../../components/content-placeholder/content-placeholder';
import { useCostTabStore } from '../../../../../../store/strategy-store/strategy-store';
import {
  setCostSelectedEpic,
  setCostSelectedTeam,
  setPerformanceActivePanel,
} from '../../../../../../store/strategy-store/strategy-store.actions';
import { Panel } from '../../../../../../store/strategy-store/strategy-store.type';
import { baseWhite } from '../../../../../../styles/design-tokens';
import { useChartData, useCostDataFetched, useCostEpics, useCostTeams, useFiltersApplied } from '../../cost-tab.hooks';
import { ChartDataType, CostMode } from '../../cost-tab.type';
import { TreeMapContent } from './tree-map-content';
import { TreeMapTooltip } from './tree-map-tooltip';

export const TreeMapChart = forwardRef<HTMLDivElement>((_, ref) => {
  const mode = useCostTabStore((state) => state.mode);

  const epics = useCostEpics();
  const teams = useCostTeams();

  const filtersApplied = useFiltersApplied();
  const dataFetched = useCostDataFetched();
  const chartData = useChartData();

  const handleClick = (data: any) => {
    const payload = data.payload || data;
    if (!payload) {
      return;
    }

    // Handle "Other" tile clicks
    if (payload.type === ChartDataType.Other) {
      if (mode === CostMode.ContributingEpics) {
        setCostSelectedEpic(null);
        setPerformanceActivePanel(Panel.OtherEpics);
      } else if (mode === CostMode.ContributingTeams) {
        setCostSelectedTeam(null);
        setPerformanceActivePanel(Panel.OtherTeams);
      }
      return;
    }

    // Handle individual epic/team clicks
    if (mode === CostMode.ContributingEpics) {
      setCostSelectedEpic(epics.find((epic) => epic.id === payload.id) ?? null);
    } else if (mode === CostMode.ContributingTeams) {
      setCostSelectedTeam(teams.find((team) => team.id === payload.id) ?? null);
    }

    setPerformanceActivePanel(Panel.Side);
  };

  if (!dataFetched) {
    return <ContentPlaceholder />;
  }

  if (!filtersApplied) {
    return <ContentPlaceholder icon="filter_list" text={i18n.t('strategy.filters.no_filters_applied')} />;
  }

  return (
    <StyledResponsiveContainer>
      <div ref={ref} style={{ width: '100%', height: '100%' }}>
        <ResponsiveContainer width="100%" height="100%">
          <Treemap
            data={chartData}
            dataKey="size"
            aspectRatio={1}
            stroke={baseWhite}
            isAnimationActive={false}
            content={<TreeMapContent />}
            onClick={handleClick}
          >
            <Tooltip content={<TreeMapTooltip />} />
          </Treemap>
        </ResponsiveContainer>
      </div>
    </StyledResponsiveContainer>
  );
});

const StyledResponsiveContainer = styled(ResponsiveContainer)`
  .recharts-wrapper {
    position: absolute !important;
  }
`;
